import React, { useEffect, useMemo, useState } from "react";
import { adminAPI } from "../services/api";
import "./AdminPromotionModal.css";

const formatDate = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleString();
};

const AdminPromotionModal = ({ isOpen, onClose }) => {
  const [search, setSearch] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promotingId, setPromotingId] = useState("");
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  const loadUsers = async (query = "") => {
    setLoading(true);
    setError("");
    try {
      const response = await adminAPI.listUsers(query);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (loadError) {
      console.error("Error loading admin user list:", loadError);
      setUsers([]);
      setError(
        loadError?.response?.data?.error || "Unable to load user profiles.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setManualEmail("");
    setError("");
    setStatus("");
    loadUsers("");
  }, [isOpen]);

  const handlePromoteById = async (profileId) => {
    if (!profileId) return;
    setPromotingId(profileId);
    setError("");
    setStatus("");

    try {
      const response = await adminAPI.promoteToAdmin({ profileId });
      setStatus(response?.data?.message || "Teacher promoted to admin.");
      await loadUsers(search.trim());
    } catch (promoteError) {
      setError(
        promoteError?.response?.data?.error || "Failed to promote teacher.",
      );
    } finally {
      setPromotingId("");
    }
  };

  const handlePromoteByEmail = async (event) => {
    event.preventDefault();
    const email = String(manualEmail || "").trim().toLowerCase();
    if (!email) return;

    setSubmittingEmail(true);
    setError("");
    setStatus("");

    try {
      const response = await adminAPI.promoteToAdmin({ email });
      setStatus(response?.data?.message || "Teacher promoted to admin.");
      setManualEmail("");
      await loadUsers(search.trim());
    } catch (promoteError) {
      setError(
        promoteError?.response?.data?.error || "Failed to promote teacher.",
      );
    } finally {
      setSubmittingEmail(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const needle = String(search || "").trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) => {
      const email = String(user.email || "").toLowerCase();
      const name = String(user.display_name || "").toLowerCase();
      return email.includes(needle) || name.includes(needle);
    });
  }, [search, users]);

  if (!isOpen) return null;

  return (
    <div className="admin-promote-modal" onClick={onClose}>
      <div className="admin-promote-panel" onClick={(event) => event.stopPropagation()}>
        <div className="admin-promote-header">
          <div>
            <h3>Admin Access Manager</h3>
            <p>Promote teacher profiles to admin role.</p>
          </div>
          <button
            type="button"
            className="admin-promote-close-btn"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="admin-promote-controls">
          <label htmlFor="admin-promote-search">
            Search profiles
            <input
              id="admin-promote-search"
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or email"
            />
          </label>
          <button
            type="button"
            className="primary-btn"
            onClick={() => loadUsers(search.trim())}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh Users"}
          </button>
        </div>

        <form className="admin-promote-manual" onSubmit={handlePromoteByEmail}>
          <label htmlFor="admin-promote-email">
            Promote by email
            <input
              id="admin-promote-email"
              type="email"
              value={manualEmail}
              onChange={(event) => setManualEmail(event.target.value)}
              placeholder="teacher@episd.org"
            />
          </label>
          <button
            type="submit"
            className="primary-btn"
            disabled={submittingEmail || !manualEmail.trim()}
          >
            {submittingEmail ? "Promoting..." : "Promote Email"}
          </button>
        </form>

        {error ? <p className="admin-promote-error">{error}</p> : null}
        {status ? <p className="admin-promote-success">{status}</p> : null}

        <div className="admin-promote-table-wrap">
          <table className="admin-promote-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Active</th>
                <th>Updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const isAdmin = String(user.role || "") === "admin";
                const isPromoting = promotingId === user.id;
                return (
                  <tr key={user.id}>
                    <td>{user.display_name || "-"}</td>
                    <td>{user.email || "-"}</td>
                    <td>{user.role || "teacher"}</td>
                    <td>{user.is_active ? "Yes" : "No"}</td>
                    <td>{formatDate(user.updated_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="primary-btn"
                        disabled={isAdmin || isPromoting}
                        onClick={() => handlePromoteById(user.id)}
                      >
                        {isAdmin ? "Admin" : isPromoting ? "Promoting..." : "Promote"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="6">No profiles found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPromotionModal;
